import { AppBox, AppGridBox, AppLink, AppText } from "../../../../../commons/components";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { AxiosError } from "axios";
import { useLayoutEffect, useState } from "react";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import AppBorderBox from "../../../../../commons/components/BorderBox";
import TournamentBasicInfoAddEdit from "./TournamentBasicInfoAddEdit";
import TournamentSpecificsAddEdit from "./TournamentSpecificsAddEdit";
import { cleanUpStepperFormData, setStepperFormData } from "../../../../../redux/slices/stepper-form-slice";
import { getErrorKeyPosition } from "../../../../../commons/utilities/form-utils";
import { IFootballCompetition } from "../../../../../commons/models/football/interface/i-football-competition";
import { ICompetitionDTO } from "../../../../../api-services/football/admin/manage-football-competition-service";
import { competitionEdit } from "../../../../../redux/slices/football/admin/competition/manage-competition-edit-slice";
import { competitionAdd } from "../../../../../redux/slices/football/admin/competition/manage-competition-add-slice";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import FormImageAddEdit from "../../../FormImageAddEdit";
import isNullOrUndefined from "../../../../../commons/utilities/null-or-undefined";
import { fetchManageFootballOrganizerList } from "../../../../../redux/slices/football/admin/organizer/manage-football-organizer-list-slice";
import { IFootballTeam } from "../../../../../commons/models/football/interface/i-football-team";
import { IHostedCompetitionDTO } from "../../../../../api-services/football/admin/manage-football-team-service";
import { hostedCompetitionEdit } from "../../../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-edit-slice";
import { hostedCompetitionAdd } from "../../../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-add-slice";

interface TournamentAddEditProps {
  competitionData?: IFootballCompetition | null;
  hostTeam?: IFootballTeam | null;
  onSave: () => void;
}

const stepperFormOptions = [
  {
    step: 1,
    title: "Tournament's Logo",
  },
  {
    step: 2,
    title: "Basic Information",
  },
  {
    step: 3,
    title: "Specifics",
  },
];

const stepperFromKeyMap = [
  ["logo"],
  ["title", "abbreviation", "organizerId", "description"],
  ["type", "teamGender", "ageGroup", "teamForm", "skillLevel"],
];

export default function TournamentAddEdit({ competitionData, hostTeam, onSave }: TournamentAddEditProps) {
  const methods = useForm();
  const formErrors = methods.formState.errors;
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const [selectedStepperFormOption, setSelectedStepperFormOption] = useState(stepperFormOptions[0]);
  const formDataCompetition = useAppSelector<ICompetitionDTO>(
    (state) => state.stepperForm.stepperFormData as ICompetitionDTO
  );
  const formDataHosted = useAppSelector<IHostedCompetitionDTO>(
    (state) => state.stepperForm.stepperFormData as IHostedCompetitionDTO
  );
  const { competitionAddResponseStatus } = useAppSelector((state) => state.footballManageCompetitionAdd);
  const { competitionEditResponseStatus } = useAppSelector((state) => state.footballManageCompetitionEdit);
  const { hostedCompetitionAddResponseStatus } = useAppSelector((state) => state.footballManageHostedCompetitionAdd);
  const { hostedCompetitionEditResponseStatus } = useAppSelector((state) => state.footballManageHostedCompetitionEdit);
  const { uploadedImageStatus } = useAppSelector((state) => state.imageUpload);

  //initiate the stepperData
  useLayoutEffect(() => {
    if (!competitionData) {
      const competitionData: ICompetitionDTO = {
        title: "",
        abbreviation: "",
        description: null,
        logo: null,
        type: "",
        teamGender: null,
        ageGroup: null,
        teamForm: null,
        skillLevel: null,
        organizerId: null,
      };
      dispatch(setStepperFormData<ICompetitionDTO>(competitionData));
    }
    if (isNullOrUndefined(hostTeam)) {
      dispatch(fetchManageFootballOrganizerList({}));
    }
  }, []);

  useLayoutEffect(() => {
    if (competitionData) {
      const competitionDTO: ICompetitionDTO = {
        title: competitionData.title,
        abbreviation: competitionData.abbreviation,
        description: competitionData.description,
        logo: competitionData.logo,
        type: competitionData.type,
        teamGender: competitionData.teamGender,
        ageGroup: competitionData.ageGroup,
        teamForm: competitionData.teamForm,
        skillLevel: competitionData.skillLevel,
        organizerId: competitionData.organizer?.id ?? null,
      };
      dispatch(setStepperFormData<ICompetitionDTO>(competitionDTO));
    }
  }, [competitionData]);

  const handleNext = (currentStep: number) => {
    if (
      competitionAddResponseStatus !== StatusEnum.Loading &&
      competitionEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep]);
    }
  };
  const handleBack = (currentStep: number) => {
    if (
      competitionAddResponseStatus !== StatusEnum.Loading &&
      competitionEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep - 2]);
    }
  };

  const handleStepperOptionClick = (option: any) => {
    if (
      competitionAddResponseStatus !== StatusEnum.Loading &&
      competitionEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(option);
    }
  };

  const handleFormSubmit = () => {
    if (hostTeam) {
      hostedTournamentSubmit(hostTeam);
    } else {
      regularTournamentSubmit();
    }
  };

  const hostedTournamentSubmit = (hostTeam: IFootballTeam) => {
    const formattedValuesHosted: IHostedCompetitionDTO = {
      ...formDataHosted,
      teamGender: Number(formDataHosted.teamGender),
    };
    dispatch(
      competitionData
        ? hostedCompetitionEdit({
            teamId: hostTeam.id,
            hostedCompetitionId: competitionData.id,
            hostedCompetitionData: formattedValuesHosted,
          })
        : hostedCompetitionAdd({ teamId: hostTeam.id, hostedCompetitionData: formattedValuesHosted })
    )
      .unwrap()
      .then(() => {
        handleSuccess();
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        handleError(error);
      });
  };
  const regularTournamentSubmit = () => {
    const formattedValues: ICompetitionDTO = {
      ...formDataCompetition,
      teamGender: Number(formDataCompetition.teamGender),
      organizerId: formDataCompetition.organizerId ?? null,
    };
    dispatch(
      competitionData
        ? competitionEdit({ competitionId: competitionData.id, competitionData: formattedValues })
        : competitionAdd({ competitionData: formattedValues })
    )
      .unwrap()
      .then(() => {
        handleSuccess();
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        handleError(error);
      });
  };

  const handleSuccess = () => {
    if (competitionData) {
      addSnackbar({
        key: "tournament-edit-success",
        text: "Tournament Edited Successfully",
        variant: "success",
      });
    } else {
      addSnackbar({
        key: "tournament-add-success",
        text: "Tournament Added Successfully",
        variant: "success",
      });
    }
    dispatch(cleanUpStepperFormData());
    onSave();
  };

  const handleError = (error: AxiosError<IServerErrorResponse>) => {
    const responseData = error.response?.data;
    if (error.response?.status === 417) {
      addSnackbar({
        key: "error",
        text: "Form not valid",
        variant: "danger",
      });
      if (responseData) {
        const stepperPositionSet = new Set<number>();
        Object.entries(responseData).forEach(([field, messages]) => {
          messages.forEach((message: string) => {
            methods.setError(field, { message });
            const stepperPosition = getErrorKeyPosition(field, stepperFromKeyMap);
            if (stepperPosition) {
              stepperPositionSet.add(stepperPosition.step);
            }
            const lowestStep = Math.min(...Array.from(stepperPositionSet));
            setSelectedStepperFormOption(stepperFormOptions[lowestStep]);
          });
        });
      }
    } else {
      addSnackbar({
        key: "error",
        text: responseData?.message,
        variant: "danger",
      });
    }
  };

  const clearErrorField = (key: string) => {
    methods.clearErrors(key);
  };

  return (
    <FormProvider {...methods}>
      <AppBox flexDirection="column" gap="sm" style={{ height: "var(--modal-md)" }}>
        <AppGridBox className=" flex-1" style={{ gridTemplateColumns: "1fr 3fr" }} gap="sm">
          <AppBorderBox border={["Right"]}>
            <AppBox flexDirection="column" gap="2xs" className="w-100">
              {stepperFormOptions.map((option, index) => (
                <AppLink
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStepperOptionClick(option);
                  }}
                >
                  <AppBorderBox
                    border={option.step === selectedStepperFormOption.step ? ["Right"] : []}
                    borderColor="primary"
                  >
                    <AppBox pl="sm" py="2xs">
                      <AppText
                        fontWeight="semibold"
                        size="lg"
                        color={option.step === selectedStepperFormOption.step ? "primary" : "currentColor"}
                      >
                        {option.title}
                      </AppText>
                    </AppBox>
                  </AppBorderBox>
                </AppLink>
              ))}
            </AppBox>
          </AppBorderBox>
          <AppBox flexDirection="column" className="w-100 flex-1">
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 1 ? { display: "flex" } : { display: "none" }}
            >
              <FormImageAddEdit
                image={competitionData?.logo}
                title="Tournament's Logo"
                DTOKey="logo"
                isEdit={!isNullOrUndefined(competitionData)}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onSkipOrNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={competitionEditResponseStatus || hostedCompetitionEditResponseStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 2 ? { display: "flex" } : { display: "none" }}
            >
              <TournamentBasicInfoAddEdit
                competitionData={competitionData}
                hostTeam={hostTeam}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onBack={handleBack}
                onNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={competitionEditResponseStatus || hostedCompetitionEditResponseStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 3 ? { display: "flex" } : { display: "none" }}
            >
              <TournamentSpecificsAddEdit
                competitionData={competitionData}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                submitStatus={
                  competitionAddResponseStatus ||
                  competitionEditResponseStatus ||
                  hostedCompetitionAddResponseStatus ||
                  hostedCompetitionEditResponseStatus
                }
                onBack={handleBack}
                onSubmit={handleFormSubmit}
                onClearError={clearErrorField}
              />
            </AppBox>
          </AppBox>
        </AppGridBox>
      </AppBox>
    </FormProvider>
  );
}
